import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from 'recharts';
import { Download, AlertTriangle, TrendingUp, Users } from 'lucide-react';
import { useApi } from '../auth/useApi';

const C = {
  primary: 'var(--c-primary)',
  secondary: 'var(--c-secondary)',
  bg: 'var(--c-bg)',
  surface: 'var(--c-surface)',
  border: 'var(--c-border)',
  text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)',
  ok: 'var(--c-ok)',
  warn: 'var(--c-warn)',
  danger: 'var(--c-danger)',
};

const CHART_COLORS = ['#1e4d3a', '#7a9b7e', '#c4793a', '#c4533a', '#a8a8a8', '#4a7a4e', '#8b6f47', '#5c8b94'];

interface Me { role: 'admin' | 'tech' | 'user' | 'viewer' | 'agent'; }

// All 14 metrics from backend
type Metric =
  | 'tickets_by_priority' | 'tickets_by_status' | 'tickets_by_category'
  | 'tickets_by_tech' | 'tickets_by_requester' | 'tickets_by_source' | 'tickets_by_form'
  | 'avg_resolution_minutes' | 'avg_first_response_minutes'
  | 'sla_compliance_pct' | 'avg_satisfaction'
  | 'weekly_volume' | 'kb_article_usage' | 'top_tags' | 'open_by_age';

interface MetricDef {
  key: Metric;
  label: string;
  group: 'Volume' | 'Time' | 'Quality' | 'Trends';
  // Default chart kind for each metric
  chart: 'bar' | 'pie' | 'line' | 'single';
}

const METRICS: MetricDef[] = [
  { key: 'tickets_by_priority',  label: 'Tickets by Priority',  group: 'Volume', chart: 'pie' },
  { key: 'tickets_by_status',    label: 'Tickets by Status',    group: 'Volume', chart: 'pie' },
  { key: 'tickets_by_category',  label: 'Tickets by Category',  group: 'Volume', chart: 'bar' },
  { key: 'tickets_by_tech',      label: 'Tickets by Tech',      group: 'Volume', chart: 'bar' },
  { key: 'tickets_by_requester', label: 'Tickets by Requester', group: 'Volume', chart: 'bar' },
  { key: 'tickets_by_source',    label: 'Tickets by Source',    group: 'Volume', chart: 'pie' },
  { key: 'tickets_by_form',      label: 'Tickets by Form',      group: 'Volume', chart: 'bar' },
  { key: 'avg_resolution_minutes',    label: 'Avg Resolution Time',     group: 'Time', chart: 'single' },
  { key: 'avg_first_response_minutes', label: 'Avg First Response Time', group: 'Time', chart: 'single' },
  { key: 'sla_compliance_pct',   label: 'SLA Compliance %',     group: 'Quality', chart: 'single' },
  { key: 'avg_satisfaction',     label: 'Avg Satisfaction by Tech', group: 'Quality', chart: 'bar' },
  { key: 'weekly_volume',        label: 'Weekly Volume Trend',  group: 'Trends', chart: 'line' },
  { key: 'kb_article_usage',     label: 'Top KB Articles',      group: 'Trends', chart: 'bar' },
  { key: 'top_tags',             label: 'Top 10 Tags',          group: 'Trends', chart: 'bar' },
  { key: 'open_by_age',          label: 'Open Tickets by Age',  group: 'Volume', chart: 'bar' },
];

interface User { UserId: number; DisplayName: string; Role: string; }

export const Reports: React.FC<{ me: Me }> = ({ me }) => {
  const api = useApi();
  const [users, setUsers] = useState<User[]>([]);
  const [slaBreaches, setSlaBreaches] = useState<{ responseBreaches: number; resolveBreaches: number; total: number } | null>(null);
  const [techPerformance, setTechPerformance] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Custom report state
  const [metric, setMetric] = useState<Metric>('tickets_by_priority');
  const [dateFrom, setDateFrom] = useState<string>(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState<string>(() => new Date().toISOString().slice(0, 10));
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [techFilter, setTechFilter] = useState<string>('');
  const [chartKind, setChartKind] = useState<'bar' | 'pie' | 'line' | 'single'>('pie');
  const [reportRows, setReportRows] = useState<any[]>([]);
  const [reportLoading, setReportLoading] = useState(false);

  const isAdminOrViewer = me.role === 'admin' || me.role === 'viewer';

  useEffect(() => {
    (async () => {
      try {
        const [u, breaches, tp] = await Promise.all([
          api.get<User[]>('/api/users'),
          api.get<any>('/api/reports/sla-breaches').catch(() => null),
          isAdminOrViewer
            ? api.get<any[]>('/api/reports/tech-performance').catch(() => [])
            : Promise.resolve([]),
        ]);
        setUsers(Array.isArray(u) ? u.filter(x => x.Role === 'admin' || x.Role === 'tech') : []);
        setSlaBreaches(breaches);
        setTechPerformance(Array.isArray(tp) ? tp : []);
      } catch (e: any) { setError(e?.message); }
      finally { setLoading(false); }
    })();
  // eslint-disable-next-line
  }, []);

  // Auto-select sensible default chart when metric changes
  useEffect(() => {
    const def = METRICS.find(m => m.key === metric);
    if (def) setChartKind(def.chart);
  }, [metric]);

  const runReport = async () => {
    setReportLoading(true);
    try {
      const body: any = { metric };
      if (dateFrom) body.dateFrom = dateFrom;
      if (dateTo) body.dateTo = dateTo;
      if (priorityFilter) body.priorityFilter = priorityFilter;
      if (techFilter) body.techFilter = parseInt(techFilter);
      const result = await api.post<{ metric: string; rows: any[] }>('/api/reports/custom', body);
      setReportRows(result.rows ?? []);
    } catch (e: any) { alert(`Report failed: ${e?.message}`); }
    finally { setReportLoading(false); }
  };

  // Auto-run on mount and when metric changes
  useEffect(() => { runReport(); /* eslint-disable-next-line */ }, [metric]);

  const exportCsv = () => {
    if (reportRows.length === 0) return;
    const headers = Object.keys(reportRows[0]);
    const csv = [
      headers.join(','),
      ...reportRows.map(r => headers.map(h => {
        const v = r[h];
        if (v === null || v === undefined) return '';
        const s = String(v).replace(/"/g, '""');
        return /[,"\n]/.test(s) ? `"${s}"` : s;
      }).join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report-${metric}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: 40, color: C.textMuted }}>Loading reports…</div>;
  if (error) return <div style={{ padding: 40, color: C.danger }}>Failed to load: {error}</div>;

  return (
    <div>
      <h1 style={{ color: C.primary, fontSize: 26, margin: '0 0 6px' }}>Reports</h1>
      <p style={{ color: C.textMuted, margin: '0 0 20px' }}>Custom queries, pre-built operational views, and exports.</p>

      {!isAdminOrViewer && (
        <div style={{
          marginBottom: 14, padding: '8px 12px', fontSize: 12, color: C.textMuted,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
        }}>Scope: your assigned tickets only.</div>
      )}

      <OverviewDashboard api={api} />

      {/* SLA Breach Tile */}
      {slaBreaches && (
        <div style={{
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 18,
          marginBottom: 16, display: 'flex', gap: 32, alignItems: 'center',
        }}>
          <div>
            <div style={{
              fontSize: 11, color: C.textMuted, fontWeight: 600,
              textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4,
            }}>SLA Breaches</div>
            <div style={{ display: 'flex', gap: 24 }}>
              <KPI label="Response" value={slaBreaches.responseBreaches} color={slaBreaches.responseBreaches > 0 ? C.danger : C.ok} />
              <KPI label="Resolution" value={slaBreaches.resolveBreaches} color={slaBreaches.resolveBreaches > 0 ? C.danger : C.ok} />
              <KPI label="Out of" value={slaBreaches.total} color={C.textMuted} />
            </div>
          </div>
          {(slaBreaches.responseBreaches > 0 || slaBreaches.resolveBreaches > 0) && (
            <div style={{
              padding: '8px 14px', background: '#fff3e0', color: '#7a4e15',
              borderRadius: 6, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6,
              border: '1px solid #e6c97a',
            }}>
              <AlertTriangle size={14} /> Action needed
            </div>
          )}
        </div>
      )}

      {/* Custom Report Builder */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 20, marginBottom: 16 }}>
        <h3 style={sectionHeaderSty}>
          <TrendingUp size={14} /> Custom Report
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
          <Field label="Metric">
            <select value={metric} onChange={e => setMetric(e.target.value as Metric)} style={inputSty()}>
              {(['Volume', 'Time', 'Quality', 'Trends'] as const).map(g => (
                <optgroup key={g} label={g}>
                  {METRICS.filter(m => m.group === g).map(m => (
                    <option key={m.key} value={m.key}>{m.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>

          <Field label="Date From">
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={inputSty()} />
          </Field>

          <Field label="Date To">
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={inputSty()} />
          </Field>

          <Field label="Priority">
            <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} style={inputSty()}>
              <option value="">All</option>
              {['Critical', 'High', 'Medium', 'Low'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>

          {isAdminOrViewer && (
            <Field label="Tech">
              <select value={techFilter} onChange={e => setTechFilter(e.target.value)} style={inputSty()}>
                <option value="">All</option>
                {users.map(u => <option key={u.UserId} value={u.UserId}>{u.DisplayName}</option>)}
              </select>
            </Field>
          )}

          <Field label="Chart Type">
            <select value={chartKind} onChange={e => setChartKind(e.target.value as any)} style={inputSty()}>
              <option value="pie">Pie</option>
              <option value="bar">Bar</option>
              <option value="line">Line</option>
              <option value="single">Single value</option>
            </select>
          </Field>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={runReport} disabled={reportLoading} style={{
              background: C.primary, color: '#fff', border: 'none', padding: '8px 14px',
              borderRadius: 6, fontSize: 13, fontWeight: 600,
              cursor: reportLoading ? 'wait' : 'pointer', opacity: reportLoading ? 0.7 : 1,
            }}>{reportLoading ? 'Running…' : 'Run Report'}</button>
            <button onClick={exportCsv} disabled={reportRows.length === 0} style={{
              background: 'transparent', color: C.text, border: `1px solid ${C.border}`,
              padding: '8px 14px', borderRadius: 6, fontSize: 13,
              cursor: reportRows.length === 0 ? 'not-allowed' : 'pointer',
              opacity: reportRows.length === 0 ? 0.5 : 1,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}><Download size={12} /> Export CSV</button>
          </div>
          <div style={{ fontSize: 12, color: C.textMuted }}>
            {reportRows.length} {reportRows.length === 1 ? 'row' : 'rows'}
          </div>
        </div>

        <ChartView rows={reportRows} chartKind={chartKind} />

        {reportRows.length > 0 && (
          <details style={{ marginTop: 16 }}>
            <summary style={{ fontSize: 12, color: C.textMuted, cursor: 'pointer', userSelect: 'none' }}>
              Show data table ({reportRows.length} rows)
            </summary>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
              <thead style={{ background: C.bg }}>
                <tr>{Object.keys(reportRows[0]).map(h => <th key={h} style={th()}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {reportRows.map((r, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                    {Object.values(r).map((v, j) => (
                      <td key={j} style={td()}>
                        {typeof v === 'number' && !Number.isInteger(v) ? v.toFixed(2) : String(v ?? '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </div>

      {/* Tech Performance (admin + viewer only) */}
      {isAdminOrViewer && techPerformance.length > 0 && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 20 }}>
          <h3 style={sectionHeaderSty}><Users size={14} /> Tech Performance</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: C.bg }}>
              <tr>
                <th style={th()}>Tech</th>
                <th style={{ ...th(), textAlign: 'right' }}>Assigned</th>
                <th style={{ ...th(), textAlign: 'right' }}>Resolved</th>
                <th style={{ ...th(), textAlign: 'right' }}>Still Open</th>
                <th style={{ ...th(), textAlign: 'right' }}>Avg Rating</th>
                <th style={{ ...th(), textAlign: 'right' }}>Total Time</th>
              </tr>
            </thead>
            <tbody>
              {techPerformance.map(t => (
                <tr key={t.UserId} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={td()}><strong>{t.DisplayName}</strong></td>
                  <td style={{ ...td(), textAlign: 'right' }}>{t.Assigned ?? 0}</td>
                  <td style={{ ...td(), textAlign: 'right', color: C.ok }}>{t.Resolved ?? 0}</td>
                  <td style={{ ...td(), textAlign: 'right', color: (t.Still_Open ?? 0) > 0 ? C.warn : C.textMuted }}>{t.Still_Open ?? 0}</td>
                  <td style={{ ...td(), textAlign: 'right' }}>
                    {t.Avg_Rating != null ? `${t.Avg_Rating.toFixed(1)} / 5` : <span style={{ color: C.textMuted }}>—</span>}
                  </td>
                  <td style={{ ...td(), textAlign: 'right', color: C.textMuted }}>
                    {t.Total_Minutes != null ? humanizeMinutes(t.Total_Minutes) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Chart renderer — adapts to chart kind
// ============================================================================
const ChartView: React.FC<{ rows: any[]; chartKind: 'bar' | 'pie' | 'line' | 'single' }> = ({ rows, chartKind }) => {
  if (rows.length === 0) {
    return (
      <div style={{
        padding: 60, textAlign: 'center', color: C.textMuted,
        background: C.bg, borderRadius: 6, border: `1px dashed ${C.border}`,
      }}>No data for this query. Try different filters or a wider date range.</div>
    );
  }

  // Single value — only when the user picked it; never auto-force based on row count
  if (chartKind === 'single') {
    const row = rows[0];
    const value = row.value ?? row.Value ?? Object.values(row).find(v => typeof v === 'number');
    const label = row.label ?? row.Label ?? Object.keys(row)[0];
    return (
      <div style={{
        padding: '40px 20px', background: C.bg, borderRadius: 6, textAlign: 'center',
      }}>
        <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 8 }}>{label}</div>
        <div style={{ fontSize: 48, fontWeight: 700, color: C.primary, lineHeight: 1 }}>
          {typeof value === 'number'
            ? (Number.isInteger(value) ? value : value.toFixed(2))
            : String(value)}
        </div>
      </div>
    );
  }

  // Normalize key casing: backend returns "label", "value", and sometimes "resolved"
  const data = rows.map(r => ({
    label: String(r.label ?? r.Label ?? ''),
    value: Number(r.value ?? r.Value ?? 0),
    resolved: Number(r.resolved ?? r.Resolved ?? 0),
  }));

  if (chartKind === 'pie') {
    return (
      <div style={{ width: '100%', height: 320 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={110} label>
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (chartKind === 'line') {
    const hasResolved = data.some(d => d.resolved > 0);
    return (
      <div style={{ width: '100%', height: 320 }}>
        <ResponsiveContainer>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.textMuted }} />
            <YAxis tick={{ fontSize: 11, fill: C.textMuted }} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="value" stroke={C.primary} strokeWidth={2} name="Created" dot />
            {hasResolved && <Line type="monotone" dataKey="resolved" stroke={C.ok} strokeWidth={2} name="Resolved" dot />}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // bar
  return (
    <div style={{ width: '100%', height: 320 }}>
      <ResponsiveContainer>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: C.textMuted }} />
          <YAxis tick={{ fontSize: 11, fill: C.textMuted }} />
          <Tooltip />
          <Bar dataKey="value" fill={C.primary} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// ============================================================================
// Overview dashboard — at-a-glance operational snapshot (v1.35)
// Self-contained: fetches its own data in parallel from /api/reports/custom.
// ============================================================================
const OverviewDashboard: React.FC<{ api: any }> = ({ api }) => {
  const [dash, setDash] = useState<Record<string, any[]> | null>(null);
  const [dashFailed, setDashFailed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const iso = (d: Date) => d.toISOString().slice(0, 10);
        const now = new Date();
        const d30 = new Date(now.getTime() - 30 * 86400000);
        const w12 = new Date(now.getTime() - 84 * 86400000);
        const run = (body: any) =>
          api.post('/api/reports/custom', body).then((r: any) => r?.rows ?? []).catch(() => []);
        const [volume, byCategory, byPriority, byAge, slaPct, avgResp] = await Promise.all([
          run({ metric: 'weekly_volume', dateFrom: iso(w12), dateTo: iso(now) }),
          run({ metric: 'tickets_by_category', dateFrom: iso(d30), dateTo: iso(now) }),
          run({ metric: 'tickets_by_priority', dateFrom: iso(d30), dateTo: iso(now) }),
          run({ metric: 'open_by_age' }),
          run({ metric: 'sla_compliance_pct', dateFrom: iso(d30), dateTo: iso(now) }),
          run({ metric: 'avg_first_response_minutes', dateFrom: iso(d30), dateTo: iso(now) }),
        ]);
        setDash({ volume, byCategory, byPriority, byAge, slaPct, avgResp });
      } catch { setDashFailed(true); }
    })();
  // eslint-disable-next-line
  }, []);

  if (dashFailed) return null;
  if (!dash) {
    return (
      <div style={{
        padding: 24, marginBottom: 16, color: C.textMuted, fontSize: 12,
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
      }}>Loading overview…</div>
    );
  }

  const firstNum = (rows: any[]): number | null => {
    if (!rows.length) return null;
    const v = Object.values(rows[0]).find(x => typeof x === 'number');
    return typeof v === 'number' ? v : null;
  };
  const openNow = dash.byAge.reduce((s: number, r: any) => s + (Number(r.value) || 0), 0);
  const sla = firstNum(dash.slaPct);
  const resp = firstNum(dash.avgResp);
  const vol = dash.volume;
  const thisWk = vol.length ? (Number(vol[vol.length - 1].value) || 0) : 0;
  const lastWk = vol.length > 1 ? (Number(vol[vol.length - 2].value) || 0) : 0;
  const delta = thisWk - lastWk;

  const Stat: React.FC<{ label: string; value: string; sub?: string; color?: string }> =
    ({ label, value, sub, color }) => (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '14px 16px' }}>
      <div style={{ fontSize: 11, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || C.text, lineHeight: 1.2, marginTop: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{sub}</div>}
    </div>
  );
  const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 16 }}>
      <div style={sectionHeaderSty}>{title}</div>
      <div style={{ height: 210 }}>{children}</div>
    </div>
  );

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 10 }}>
        <Stat label="Open tickets" value={String(openNow)} color={openNow > 0 ? C.warn : C.ok} sub="unresolved right now" />
        <Stat label="SLA compliance" value={sla != null ? `${Math.round(sla)}%` : '—'}
              color={sla != null && sla < 90 ? C.danger : C.ok} sub="last 30 days" />
        <Stat label="Avg first response" value={resp != null ? humanizeMinutes(Math.round(resp)) : '—'} sub="last 30 days" />
        <Stat label="Tickets this week" value={String(thisWk)} color={delta > 0 ? C.warn : C.ok}
              sub={`${delta >= 0 ? '+' : ''}${delta} vs last week`} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 10 }}>
        <ChartCard title="Weekly volume · 12 weeks">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={vol}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} tickFormatter={(v: any) => String(v).slice(5, 10)} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke={CHART_COLORS[0]} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Open tickets by age">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dash.byAge}>
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill={CHART_COLORS[2]} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="By category · 30 days">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dash.byCategory}>
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="value" fill={CHART_COLORS[1]} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Priority mix · 30 days">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={dash.byPriority} dataKey="value" nameKey="label" outerRadius={75} label>
                {dash.byPriority.map((_: any, i: number) => (
                  <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
};

// ============================================================================
// Small helpers
// ============================================================================
const KPI: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div>
    <div style={{ fontSize: 11, color: C.textMuted }}>{label}</div>
    <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
  </div>
);

const humanizeMinutes = (m: number): string => {
  if (!m) return '0m';
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${(m / 60).toFixed(1)}h`;
  return `${(m / 1440).toFixed(1)}d`;
};

const sectionHeaderSty: React.CSSProperties = {
  margin: '0 0 14px', fontSize: 12, color: C.textMuted,
  textTransform: 'uppercase', letterSpacing: '0.4px', fontWeight: 600,
  display: 'inline-flex', alignItems: 'center', gap: 6,
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div style={{
      fontSize: 11, fontWeight: 600, color: C.textMuted,
      marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>{label}</div>
    {children}
  </div>
);

const inputSty = (): React.CSSProperties => ({
  border: `1px solid ${C.border}`, padding: '7px 10px', borderRadius: 6,
  fontSize: 13, width: '100%', boxSizing: 'border-box', background: C.bg,
});

const th = (): React.CSSProperties => ({
  padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600,
  color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px',
});
const td = (): React.CSSProperties => ({ padding: '10px 12px', verticalAlign: 'middle' });
